import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { MdAdd, MdEdit, MdDelete, MdLock, MdSearch } from 'react-icons/md';
import { Utils, authJsonHeaders, handleSessionExpired, formatDateTime } from '../../utils';
import Modal from '../common/Modal';

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const btnPrimary = {
  padding: '10px 18px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};

const btnGhost = {
  padding: '10px 18px',
  background: '#f3f4f6',
  color: '#374151',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '14px',
};

const emptyForm = { name: '', status: 1, permission_ids: [] };

const AdminRoles = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState('');

  const permissionsByModule = useMemo(() => {
    const map = {};
    permissions.forEach((p) => {
      if (!map[p.module]) map[p.module] = [];
      map[p.module].push(p);
    });
    return map;
  }, [permissions]);

  useEffect(() => {
    fetchPermissions();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await fetch(Utils.baseurl + 'admin/permissions', {
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) setPermissions(result.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRoles = async (q = search) => {
    setLoading(true);
    try {
      const response = await fetch(Utils.baseurl + 'admin/roles', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({ search: q }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) setRoles(result.data || []);
      else toast.error(result.message || 'Failed to load roles');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load roles');
    }
    setLoading(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFieldError('');
    setShowModal(true);
  };

  const openEdit = (role) => {
    if (role.is_system) {
      toast.info('Fixed system roles cannot be changed');
      return;
    }
    setEditing(role);
    setForm({
      name: role.name || '',
      status: role.status ?? 1,
      permission_ids: role.permission_ids || [],
    });
    setFieldError('');
    setShowModal(true);
  };

  const togglePermission = (id) => {
    setForm((prev) => {
      const set = new Set(prev.permission_ids);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, permission_ids: [...set] };
    });
  };

  const toggleModule = (modulePerms) => {
    const ids = modulePerms.map((p) => p.id);
    const allSelected = ids.every((id) => form.permission_ids.includes(id));
    setForm((prev) => {
      const set = new Set(prev.permission_ids);
      if (allSelected) ids.forEach((id) => set.delete(id));
      else ids.forEach((id) => set.add(id));
      return { ...prev, permission_ids: [...set] };
    });
  };

  const handleSave = async () => {
    setFieldError('');
    if (!form.name.trim()) {
      setFieldError('Role name is required');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(Utils.baseurl + 'admin/roles/save', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({
          id: editing?.id || undefined,
          name: form.name.trim(),
          status: form.status,
          permission_ids: form.permission_ids,
        }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        toast.success(result.message || 'Saved');
        setShowModal(false);
        fetchRoles();
      } else {
        toast.error(result.message || 'Save failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (role) => {
    if (role.is_system) {
      toast.info('Fixed system roles cannot be deleted');
      return;
    }
    const confirm = await Swal.fire({
      title: 'Delete role?',
      text: `"${role.name}" will be permanently deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Delete',
    });
    if (!confirm.isConfirmed) return;
    try {
      const response = await fetch(Utils.baseurl + 'admin/roles/delete', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({ id: role.id }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        toast.success(result.message || 'Deleted');
        fetchRoles();
      } else {
        toast.error(result.message || 'Delete failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Delete failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, color: '#111827' }}>Roles & Permissions</h1>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 14 }}>
            Create superadmin_team roles and assign permissions. Fixed system roles cannot be changed or deleted.
          </p>
        </div>
        <button type="button" style={btnPrimary} onClick={openCreate}>
          <MdAdd size={20} /> Create Role
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <MdSearch size={18} style={{ position: 'absolute', left: 12, top: 13, color: '#9ca3af' }} />
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Search roles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchRoles(search); }}
          />
        </div>
        <button type="button" style={btnGhost} onClick={() => fetchRoles(search)}>Search</button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
              {['Role', 'Permissions', 'Status', 'Updated', 'Actions'].map((h) => (
                <th key={h} style={{ padding: '14px 16px', fontSize: 12, color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
            ) : roles.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>No roles found</td></tr>
            ) : roles.map((role) => (
              <tr key={role.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {role.name}
                    {role.is_system ? <MdLock size={14} color="#9ca3af" title="Fixed system role" /> : null}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{role.slug}</div>
                </td>
                <td style={{ padding: '14px 16px', fontSize: 13, color: '#374151' }}>
                  {(role.permissions || []).length} assigned
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{
                    fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                    background: role.status === 1 ? '#dcfce7' : '#fee2e2',
                    color: role.status === 1 ? '#166534' : '#991b1b',
                  }}>
                    {role.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', fontSize: 13, color: '#6b7280' }}>
                  {role.updatedAt ? formatDateTime(role.updatedAt) : '—'}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      title={role.is_system ? 'Locked' : 'Edit'}
                      disabled={role.is_system}
                      onClick={() => openEdit(role)}
                      style={{
                        ...btnGhost, padding: '8px', opacity: role.is_system ? 0.45 : 1,
                        cursor: role.is_system ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <MdEdit size={18} />
                    </button>
                    <button
                      type="button"
                      title={role.is_system ? 'Locked' : 'Delete'}
                      disabled={role.is_system}
                      onClick={() => handleDelete(role)}
                      style={{
                        ...btnGhost, padding: '8px', color: '#dc2626',
                        opacity: role.is_system ? 0.45 : 1,
                        cursor: role.is_system ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <Modal
          title={editing ? 'Edit Role' : 'Create Role'}
          onClose={() => setShowModal(false)}
          maxWidth="720px"
          footer={(
            <>
              <button type="button" style={btnGhost} onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button type="button" style={btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Role' : 'Create Role')}
              </button>
            </>
          )}
        >
          <div style={{ marginBottom: 16, padding: '10px 12px', background: '#eef2ff', borderRadius: 8, fontSize: 13, color: '#3730a3' }}>
            New roles are saved as type <strong>superadmin_team</strong>.
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151' }}>Role name *</label>
            <input
              style={inputStyle}
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Billing Manager"
            />
            {fieldError ? <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{fieldError}</div> : null}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151' }}>Status</label>
            <select
              style={inputStyle}
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label style={{ fontWeight: 600, color: '#111827' }}>Permissions</label>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{form.permission_ids.length} selected</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
              {Object.keys(permissionsByModule).map((module) => {
                const list = permissionsByModule[module];
                const allSelected = list.every((p) => form.permission_ids.includes(p.id));
                return (
                  <div key={module} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#374151', marginBottom: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={allSelected} onChange={() => toggleModule(list)} />
                      {module}
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {list.map((p) => (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#4b5563', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={form.permission_ids.includes(p.id)}
                            onChange={() => togglePermission(p.id)}
                            style={{ marginTop: 2 }}
                          />
                          <span>
                            <span style={{ display: 'block', color: '#111827' }}>{p.name}</span>
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>{p.key}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default AdminRoles;
